import { canonicalLanguageTag, type LanguageTag, parentLanguages } from './languageTag.ts'

export type { LanguageTag } from './languageTag.ts'
export { canonicalLanguageTag } from './languageTag.ts'

export const MAX_LOCALIZED_LANGUAGES = 32
export const MAX_LOCALIZED_LENGTH = 65536

export interface LocalizedText {
  sourceLanguage: LanguageTag
  values: Record<LanguageTag, string>
}

export interface LocalizedFieldPolicy {
  required: boolean
  maxLanguages: number
  maxLength: number
}

export interface LocalizedTextResolutionPolicy {
  mode: 'administrative' | 'patient-output'
  allowParent: boolean
}

export type ResolvedLocalizedText =
  | { text: string; requestedLanguage: LanguageTag; actualLanguage: LanguageTag; reason: 'exact' | 'parent' | 'source' | 'remaining' }
  | { text: string; requestedLanguage: LanguageTag; actualLanguage: null; reason: 'caller' }
  | { text: null; requestedLanguage: LanguageTag; actualLanguage: null; reason: 'absent' }

/** Evidence vocabulary only. Domains own provider admission and revision identity. */
export interface LocalizedReferenceSnapshot {
  provider: string
  id: string
  code: string
  sourceRevision: string
  name: LocalizedText
  digest: string
}

const nonblank = (value: string) => /[^\p{White_Space}]/u.test(value)
const validText = (value: string, limit: number) => {
  let length = 0
  for (const scalar of value) {
    const code = scalar.codePointAt(0)!
    if (code === 0 || (code >= 0xd800 && code <= 0xdfff) || ++length > limit) return false
  }
  return true
}
const object = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value)

function normalizedMap(raw: unknown, maxLanguages: number, maxLength: number, sparse: boolean): LocalizedText {
  if (!object(raw) || Object.keys(raw).sort().join(',') !== 'sourceLanguage,values' || typeof raw.sourceLanguage !== 'string' || !object(raw.values)) {
    throw new Error('invalid localized text shape')
  }
  const sourceLanguage = canonicalLanguageTag(raw.sourceLanguage)
  const entries = Object.entries(raw.values)
  if (entries.length > maxLanguages) throw new Error('too many localized values')
  const values = Object.create(null) as Record<LanguageTag, string>
  for (const [key, value] of entries) {
    const tag = canonicalLanguageTag(key)
    if (Object.hasOwn(values, tag)) throw new Error('duplicate canonical language')
    if (typeof value !== 'string' || !validText(value, maxLength) || (!sparse && !nonblank(value))) throw new Error('invalid localized value')
    values[tag] = value
  }
  return { sourceLanguage, values }
}

/** Validate a write, preserve text verbatim, normalize tags; never mutate input. */
export function normalizeLocalizedText(raw: unknown, policy: LocalizedFieldPolicy): LocalizedText | null {
  if (typeof policy.required !== 'boolean' || !Number.isInteger(policy.maxLanguages) || policy.maxLanguages < 1 || policy.maxLanguages > MAX_LOCALIZED_LANGUAGES || !Number.isInteger(policy.maxLength) || policy.maxLength < 1 || policy.maxLength > MAX_LOCALIZED_LENGTH) {
    throw new Error('invalid localized field policy')
  }
  if (raw === null || raw === undefined) {
    if (policy.required) throw new Error('localized text required')
    return null
  }
  const value = normalizedMap(raw, policy.maxLanguages, policy.maxLength, false)
  const source = value.values[value.sourceLanguage]
  if (!source || !nonblank(source)) throw new Error('source value required')
  return value
}

export interface LocalizedTextDigests { source: string; full: string }

/** Length-framed UTF-8 is independent of JSON escaping and object key order. */
export async function localizedTextDigests(raw: unknown, policy: LocalizedFieldPolicy): Promise<LocalizedTextDigests> {
  const value = normalizeLocalizedText(raw, { ...policy, required: true })!
  const encoder = new TextEncoder()
  const frame = (text: string) => `${encoder.encode(text).length}:${text}`
  const hash = async (text: string) => {
    const bytes = await crypto.subtle.digest('SHA-256', encoder.encode(text))
    return Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2, '0')).join('')
  }
  const source = frame(value.sourceLanguage)
  const full = source + Object.keys(value.values).sort().map(key => frame(key) + frame(value.values[key as LanguageTag]!)).join('')
  return {
    source: await hash(`localized-text/source/v1\0${source}${frame(value.values[value.sourceLanguage]!)}`),
    full: await hash(`localized-text/full/v1\0${full}`),
  }
}

/** Sparse drafts/projections are readable; only normalizeLocalizedText admits writes. */
export function resolveLocalizedText(raw: unknown, requested: string, policy: LocalizedTextResolutionPolicy, fallback = ''): ResolvedLocalizedText {
  const requestedLanguage = canonicalLanguageTag(requested)
  if (!['administrative', 'patient-output'].includes(policy.mode) || typeof policy.allowParent !== 'boolean') throw new Error('invalid resolution policy')
  if (raw !== null && raw !== undefined) {
    const value = normalizedMap(raw, MAX_LOCALIZED_LANGUAGES, MAX_LOCALIZED_LENGTH, true)
    const choose = (actualLanguage: LanguageTag, reason: Extract<ResolvedLocalizedText, { actualLanguage: LanguageTag }>['reason']): ResolvedLocalizedText | null => {
      const text = value.values[actualLanguage]
      return text && nonblank(text) ? { text, requestedLanguage, actualLanguage, reason } : null
    }
    const exact = choose(requestedLanguage, 'exact')
    if (exact) return exact
    if (policy.mode === 'administrative' || policy.allowParent) {
      for (const parent of parentLanguages(requestedLanguage)) {
        const resolved = choose(parent, 'parent')
        if (resolved) return resolved
      }
    }
    const source = choose(value.sourceLanguage, 'source')
    if (source) return source
    if (policy.mode === 'administrative') {
      for (const language of Object.keys(value.values).sort()) {
        const remaining = choose(language as LanguageTag, 'remaining')
        if (remaining) return remaining
      }
    }
  }
  if (policy.mode === 'administrative' && nonblank(fallback)) {
    if (!validText(fallback, MAX_LOCALIZED_LENGTH)) throw new Error('invalid caller fallback')
    return { text: fallback, requestedLanguage, actualLanguage: null, reason: 'caller' }
  }
  return { text: null, requestedLanguage, actualLanguage: null, reason: 'absent' }
}
